import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import useCallbackAsync from 'hooks/useCallbackAsync';
import api from 'api';
import CheckToggleField from 'components/CheckToggleField';
import InputField from 'components/InputField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import EmailOrPhoneFieldWrapper from 'components/EmailOrPhoneFieldWrapper';
import { User } from 'models/User';
import NoUsersFound from '../../../../../AdminDashboard/components/NoUsersFound';
import {
  getCheckedValuesInObject,
  getEmailOrPhoneForLoginOrRegister,
  getUserFullNameOrEmail,
  getJoinedWordsWithComma,
  getUserFullName,
  renderFullUserInfo,
} from 'utils';
import { EmailOrPhoneForm } from 'models/AuthBody';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';

import './AddBossModeAccount.styles.scss';

const LIMIT = '20';

export type AddBossModeAccountForm = {
  transformExistingAccountBossMode?: boolean;
  selectedUsersIds?: Record<string, any>;
  firstName?: string;
  lastName?: string;
  searchString?: string;
} & EmailOrPhoneForm;

type AddBossModeAccountProps = {
  className?: string;
  onAddBossMode: () => void;
};

const AddBossModeAccount: React.ForwardRefRenderFunction<
  ModalRef,
  AddBossModeAccountProps
> = (props, ref) => {
  const { className, onAddBossMode } = props;

  const classes = classNames('add-boss-mode-account', className);

  const [modal, modalRef] = useCallbackRef<ModalRef>(null);

  const [searchResults, setSearchResults] = useState<User[]>([]);

  const hasFetched = useRef(false);

  useImperativeHandle(ref, () => modal, [modal]);

  const { popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();
  const commonValidations = useValidations();

  const getFullNames = useCallback(
    (selectedUsersIds: string[]) => {
      const users = searchResults
        ?.filter(({ _id }) => selectedUsersIds.includes(_id))
        .map(getUserFullNameOrEmail);

      return getJoinedWordsWithComma(users);
    },
    [searchResults],
  );
  const [onSubmit] = useCallbackAsync(
    async (values: AddBossModeAccountForm) => {
      const {
        transformExistingAccountBossMode,
        firstName,
        lastName,
        email,
        emailOrPhone,
      } = values;

      const selectedUsersIds = getCheckedValuesInObject(
        values.selectedUsersIds,
      );

      if (transformExistingAccountBossMode) {
        await api.portfolio.admin.promoteBossModeAccount(selectedUsersIds);
      } else {
        await api.portfolio.admin.addBossModeAccount({
          firstName,
          lastName,
          ...getEmailOrPhoneForLoginOrRegister({ email, emailOrPhone }),
        });
      }

      const fullName = transformExistingAccountBossMode
        ? getFullNames(selectedUsersIds)
        : getUserFullName(firstName, lastName);

      popSuccess(
        t(
          transformExistingAccountBossMode
            ? 'userPromotedToBM'
            : 'userInvitedAsBM',
          {
            fullName,
          },
        ),
      );

      onAddBossMode();
      modal?.close();
    },
  );

  const [onSearchUsers, isLoadingUsers] = useCallbackAsync(
    async (searchString: string) => {
      const {
        data: { items },
      } = await api.portfolio.admin.searchUsersForBossModeAccount({
        $searchString: searchString,
        $limit: LIMIT,
      });

      setSearchResults(items);
      hasFetched.current = true;
    },
  );

  const onClose = useCallback(() => {
    hasFetched.current = false;
    setSearchResults(null);
  }, []);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('addBossModeAccount')}
      onClose={onClose}
    >
      <Form
        onSubmit={onSubmit}
        render={({
          submitting,
          invalid,
          values: {
            transformExistingAccountBossMode,
            searchString,
            selectedUsersIds,
          },
          form: { change },
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Field
              name="transformExistingAccountBossMode"
              type="checkbox"
              component={CheckToggleField}
              label={t('transformExistingAccountBossMode')}
            />

            {transformExistingAccountBossMode ? (
              <>
                <div className="row">
                  <Field
                    className="search-input"
                    name="searchString"
                    component={InputField}
                    label={t('searchUsersByNameOrEmail')}
                  />

                  <Button
                    disabled={submitting || isLoadingUsers}
                    onClick={() => {
                      change('selectedUsersIds', undefined);
                      onSearchUsers(searchString);
                    }}
                  >
                    {t('search')}
                  </Button>
                </div>

                {!isLoadingUsers &&
                  hasFetched.current &&
                  (!searchResults?.length ? (
                    <NoUsersFound />
                  ) : (
                    <CheckboxFieldGroup
                      name="selectedUsersIds"
                      required
                      options={searchResults.map((user) => ({
                        value: user._id,
                        label: renderFullUserInfo(user),
                      }))}
                    />
                  ))}
              </>
            ) : (
              <>
                <div className="row">
                  <Field
                    name="firstName"
                    component={InputField}
                    label={t('firstName')}
                    required
                    validate={commonValidations.name}
                  />
                  <Field
                    name="lastName"
                    component={InputField}
                    label={t('lastName')}
                    required
                    validate={commonValidations.name}
                  />
                </div>
                <div className="row">
                  <EmailOrPhoneFieldWrapper />
                </div>
              </>
            )}

            <div className="actions">
              <Button
                type="submit"
                disabled={
                  invalid ||
                  submitting ||
                  isLoadingUsers ||
                  (transformExistingAccountBossMode && !selectedUsersIds)
                }
              >
                {t(
                  transformExistingAccountBossMode
                    ? 'confirm'
                    : 'inviteBossMode',
                )}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(AddBossModeAccount);
